import React, { useEffect, useState } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * Composant de contrôle d'accès basé sur les permissions
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.permissions - Permissions requises
 * @param {string} props.permissions.resource - Ressource concernée
 * @param {string} props.permissions.action - Action requise (read/write/create)
 * @param {ReactNode} props.children - Contenu à afficher si les permissions sont suffisantes
 * @param {ReactNode} props.fallback - Contenu à afficher si les permissions sont insuffisantes
 * @param {boolean} props.requireAll - Si true, toutes les permissions doivent être satisfaites
 * @returns {ReactNode} Le contenu approprié en fonction des permissions
 */
const PermissionGate = ({ 
  permissions, 
  children, 
  fallback = null,
  requireAll = true,
  ...props 
}) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const { hasPermission, hasAllPermissions, hasAnyPermission, loading: permissionsLoading } = usePermissions({ 
    userId: user?.uid 
  });

  const loading = authLoading || permissionsLoading;

  // Si en cours de chargement, afficher un fallback ou rien
  if (loading) {
    return fallback || null;
  }

  // Si pas d'utilisateur connecté, afficher le fallback
  if (!user) {
    return fallback;
  }

  let hasRequiredPermissions = false;

  if (Array.isArray(permissions)) {
    // Multiple permissions
    if (requireAll) {
      hasRequiredPermissions = hasAllPermissions(permissions);
    } else {
      hasRequiredPermissions = hasAnyPermission(permissions);
    }
  } else if (permissions && permissions.resource && permissions.action) {
    // Single permission
    hasRequiredPermissions = hasPermission(permissions);
  } else {
    // Pas de permissions spécifiées, accès refusé par défaut
    hasRequiredPermissions = false;
  }

  if (hasRequiredPermissions) {
    return React.cloneElement(React.Children.only(children), props);
  }

  return fallback;
};

export default PermissionGate;
